import { Dispatch, SetStateAction } from 'react';

import { BlobServiceClient, BlockBlobClient } from '@azure/storage-blob';
import { AxiosResponse } from 'axios';

import {
  LOADING_DELAY,
  MAX_ASSET_UPLOAD_RETRIES,
  UPLOAD_DELAY,
  UPLOAD_PICKER_TYPES
} from '@/constants';
import { AssetType } from '@/enums';
import { showToast, ToastType } from '@/helpers/toast';
import i18n from '@/i18n';
import { AssetUploadErrorType } from '@/types';

import { getValidationFormatsFromPickerTypes } from './UploadFileHelper';

const IMAGE_VALIDATION_FORMATS = getValidationFormatsFromPickerTypes(UPLOAD_PICKER_TYPES.IMAGE);
const VIDEO_VALIDATION_FORMATS = getValidationFormatsFromPickerTypes(UPLOAD_PICKER_TYPES.VIDEO);
const AUDIO_VALIDATION_FORMATS = getValidationFormatsFromPickerTypes(UPLOAD_PICKER_TYPES.AUDIO);

export const validateAssetForUpload = (
  asset: File,
  acceptFormat: string[]
): AssetUploadErrorType | null => {
  const extension = getAssetExtension(asset.name).toLowerCase();
  const mimeTypeValid = asset.type
    ? acceptFormat.some((format) => asset.type.startsWith(format.replace('/*', '/')))
    : false;
  const extensionValid = extension
    ? acceptFormat.some((format) => format === extension || format === extension.replace('.', ''))
    : false;

  if (!mimeTypeValid && !extensionValid) {
    return AssetUploadErrorType.UNSUPPORTED_MEDIA_FORMAT;
  }

  if (asset.size > window.MAX_ASSET_UPLOAD_SIZE) {
    return AssetUploadErrorType.MAX_FILE_SIZE_EXCEEDED;
  }

  return null;
};

export const uploadAsset = async (
  assetBlobClient: BlockBlobClient | undefined,
  uploadedAsset: File,
  setIsUploading?: Dispatch<SetStateAction<boolean>>
) => {
  if (!assetBlobClient) {
    setIsUploading?.(false);
    return false;
  }

  const didUploadSucceed = await tryUploadAsset(
    uploadedAsset,
    assetBlobClient,
    i18n.t('assetError', { ns: 'common' })
  );
  if (!didUploadSucceed) {
    setIsUploading?.(false);
    return false;
  }

  return true;
};

export const getAssetType = (asset: File) => {
  if (asset.type) {
    const assetType = getAssetTypeByType(asset);
    if (assetType) {
      return assetType;
    }
    return getAssetTypeByName(asset);
  }
  if (asset.name) {
    return getAssetTypeByName(asset);
  }
};

export const getAssetTypeByName = (asset: File) => {
  const extension = getAssetExtension(asset.name).toLowerCase().replace('.', '');
  if (!extension) {
    return;
  }
  if (IMAGE_VALIDATION_FORMATS.includes(extension)) {
    return AssetType.IMAGE;
  }
  if (VIDEO_VALIDATION_FORMATS.includes(extension)) {
    return AssetType.VIDEO;
  }
  if (AUDIO_VALIDATION_FORMATS.includes(extension)) {
    return AssetType.AUDIO;
  }
};

const getAssetTypeByType = (asset: File) => {
  if (asset.type.startsWith('image/')) {
    return AssetType.IMAGE;
  } else if (asset.type.startsWith('video/')) {
    return AssetType.VIDEO;
  } else if (asset.type.startsWith('audio/')) {
    return AssetType.AUDIO;
  }
  return null;
};

export const getAssetBlobClient = (data: AxiosResponse) => {
  if (!data) {
    return;
  }

  return new BlobServiceClient(data.data.baseUrl + getToken(data.data.token))
    .getContainerClient('')
    .getBlockBlobClient(data.data.path);
};

const getToken = (token: string) => {
  return token.startsWith('?') ? token : `?${token}`;
};

export const getAssetUrl = (data: AxiosResponse) => {
  return `${data.data.baseUrl}/${data.data.path}`;
};

export const getVideoThumbnail = (asset: Blob | MediaSource) => {
  return new Promise((resolve, reject) => {
    const videoPlayer = document.createElement('video');
    videoPlayer.setAttribute('src', URL.createObjectURL(asset));
    videoPlayer.crossOrigin = 'anonymous';
    videoPlayer.load();

    let hasSeeked = false;
    let hasSetCurrentTime = false;

    const handleError = () => {
      cleanup();
      reject('error when loading video asset');
    };

    const handleCanPlay = () => {
      if (!hasSetCurrentTime) {
        hasSetCurrentTime = true;
        videoPlayer.currentTime = videoPlayer.duration / 2;
      }
    };

    const handleSeeked = () => {
      if (hasSeeked) {
        return;
      }
      hasSeeked = true;

      setTimeout(() => {
        requestAnimationFrame(() => {
          getThumbnailFromImage(
            videoPlayer,
            videoPlayer.videoWidth,
            videoPlayer.videoHeight,
            resolve
          );
          cleanup();
        });
      }, LOADING_DELAY);
    };

    const cleanup = () => {
      videoPlayer.removeEventListener('error', handleError);
      videoPlayer.removeEventListener('canplay', handleCanPlay);
      videoPlayer.removeEventListener('seeked', handleSeeked);
      URL.revokeObjectURL(videoPlayer.src);
    };

    videoPlayer.addEventListener('error', handleError);
    videoPlayer.addEventListener('canplay', handleCanPlay);
    videoPlayer.addEventListener('seeked', handleSeeked);
  });
};

export const getImageThumbnail = (asset: Blob | MediaSource) => {
  return new Promise((resolve) => {
    const image = new Image();
    image.src = URL.createObjectURL(asset);

    image.addEventListener('load', () => {
      getThumbnailFromImage(image, image.width, image.height, resolve);
      URL.revokeObjectURL(image.src);
    });
  });
};

const getThumbnailFromImage = (
  image: HTMLVideoElement | HTMLImageElement,
  imageWidth: number,
  imageHeight: number,
  resolve: (value: unknown) => void
) => {
  const canvas = document.createElement('canvas');

  const aspectRatio = imageWidth / imageHeight;
  let width;
  let height;

  if (aspectRatio >= 1) {
    width = window.MAX_THUMBNAIL_SIZE.width;
    height = window.MAX_THUMBNAIL_SIZE.height / aspectRatio;
  } else {
    width = window.MAX_THUMBNAIL_SIZE.width * aspectRatio;
    height = window.MAX_THUMBNAIL_SIZE.height;
  }

  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.drawImage(image, 0, 0, width, height);
    ctx.canvas.toBlob(
      (blob) => {
        resolve(blob);
      },
      'image/png',
      0.85
    );
  }
};

export const bytesToMegabytes = (bytes: number) => {
  const megabytes = divideWithMillion(bytes);
  const gigabytes = megabytes / 1024;

  return gigabytes >= 1 ? `${gigabytes.toFixed(2)} GB` : `${megabytes.toFixed(2)} MB`;
};
export const divideWithMillion = (num: number) => num / 1048576;

export const checkIfAssetNeedsThumbnail = (assetType: AssetType) => assetType !== AssetType.AUDIO;

export const getThumbnailFromType = async (uploadedAsset: File) => {
  const assetType = getAssetType(uploadedAsset);
  switch (assetType) {
    case AssetType.IMAGE:
      return {
        file: (await getImageThumbnail(uploadedAsset)) as File
      };
    case AssetType.VIDEO:
      return {
        file: (await getVideoThumbnail(uploadedAsset)) as File
      };
  }
};

export const tryUploadAsset = async (
  asset: File,
  blobClient: BlockBlobClient,
  errorMessage: string
) => {
  for (let i = 0; i < MAX_ASSET_UPLOAD_RETRIES; i++) {
    if (i > 0) {
      await new Promise((resolve) => setTimeout(resolve, UPLOAD_DELAY));
    }

    const assetName = encodeURIComponent(asset.name)
      .replace(/['()]/g, escape)
      .replace(/\*/g, '%2A')
      .replace(/%20/g, ' ');

    const contentDisposition = `attachment; filename="${assetName}"`;

    try {
      await blobClient.uploadBrowserData(asset, {
        blobHTTPHeaders: {
          blobContentDisposition: contentDisposition
        }
      });
      return true;
    } catch {
      showToast(ToastType.INFO, i18n.t('retry', { ns: 'common' }));
    }
  }

  showToast(ToastType.ERROR, i18n.t('uploadError', { ns: 'common', error: errorMessage }));

  return false;
};

export const getAssetExtensionFromPath = (assetPath: string) => {
  return '.' + assetPath.slice(((assetPath.lastIndexOf('.') - 1) >>> 0) + 2);
};

export const getAssetNameFromPath = (assetPath: string) => {
  const parts = assetPath.split('/');
  const assetNameWithExtension = parts[parts.length - 1];

  return assetNameWithExtension.split('.').slice(0, -1).join('.');
};

export const getAssetExtension = (assetName: string) => {
  const parts = assetName.split('.');
  if (parts.length <= 1) {
    return '';
  }

  return `.${parts.pop()}`;
};

export const getAssetExtensionFromType = (assetType: AssetType) => {
  switch (assetType.toString().toUpperCase()) {
    case AssetType[AssetType.IMAGE]:
      return '.png';
    case AssetType[AssetType.VIDEO]:
      return '.mp4';
    case AssetType[AssetType.AUDIO]:
      return '.mp3';
    default:
      return '';
  }
};

export const removeFileExtension = (fileName: string) => fileName.replace(/\.[^/.]+$/, '');

export const isSameUrlIgnoringSas = (url?: string, otherUrl?: string): boolean =>
  !!url && !!otherUrl && url.split('?')[0] === otherUrl.split('?')[0];
